/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/itpPlacements.ts
 * FILE_ID: JOBHUB-QA-ITP-PLACEMENTS-001
 * PURPOSE: Turn editor marks into the server stamp list.
 */
import type { EditorItem } from '@quotech/email-hub/feature/components/_pdfTypes'
import { bytesToBase64 } from './bytesToBase64'

export type ItpPlacement =
  | { kind: 'image'; png_base64: string; page: string | number; x_pt: number; y_pt: number; width_pt: number; height_pt: number }
  | { kind: 'text'; text: string; page: string | number; x_pt: number; y_pt: number; font_size_pt: number }
  | { kind: 'highlight'; page: string | number; x_pt: number; y_pt: number; width_pt: number; height_pt: number }

function pageToken(index: number, pageCount: number): 'first' | 'last' | number {
  if (index <= 0) return 'first'
  if (pageCount > 1 && index >= pageCount - 1) return 'last'
  return index
}

function keyOf(item: EditorItem): string {
  if (item.kind === 'text') {
    return `text:${item.text}:${item.xPt}:${item.yPt}:${item.fontSizePt}`
  }
  if (item.kind === 'highlight') {
    return `highlight:${item.xPt}:${item.yPt}:${item.widthPt}:${item.heightPt}`
  }
  return `image:${bytesToBase64(item.imageBytes)}:${item.xPt}:${item.yPt}:${item.widthPt}:${item.heightPt}`
}

function one(item: EditorItem, page: string | number): ItpPlacement {
  if (item.kind === 'text') {
    return { kind: 'text', text: item.text, page, x_pt: item.xPt, y_pt: item.yPt, font_size_pt: item.fontSizePt }
  }
  if (item.kind === 'highlight') {
    return { kind: 'highlight', page, x_pt: item.xPt, y_pt: item.yPt, width_pt: item.widthPt, height_pt: item.heightPt }
  }
  return {
    kind: 'image',
    png_base64: bytesToBase64(item.imageBytes),
    page,
    x_pt: item.xPt,
    y_pt: item.yPt,
    width_pt: item.widthPt,
    height_pt: item.heightPt,
  }
}

export function toPlacements(items: EditorItem[], pageCount: number): ItpPlacement[] {
  const groups = new Map<string, { item: EditorItem; pages: number[] }>()
  for (const item of items) {
    const key = keyOf(item)
    const group = groups.get(key) ?? { item, pages: [] }
    group.pages.push(item.pageIndex)
    groups.set(key, group)
  }
  const placements: ItpPlacement[] = []
  for (const group of groups.values()) {
    const pages = [...new Set(group.pages)].sort((left, right) => left - right)
    const coversEveryPage = pageCount > 1 && pages.length === pageCount && pages.every((page, index) => page === index)
    if (coversEveryPage) {
      placements.push(one(group.item, 'every'))
      continue
    }
    for (const page of pages) placements.push(one(group.item, pageToken(page, pageCount)))
  }
  return placements
}

export function signedPagesLabel(placements: ItpPlacement[], pageCount: number): string {
  const pages = new Set(placements.map((row) => row.page))
  if (pages.has('every')) return 'All pages'
  if (pages.size === 1 && pages.has('first')) return 'Cover page'
  if (pages.size === 1 && pages.has('last')) return 'End page'
  if (pages.size === 2 && pages.has('first') && pages.has('last')) return 'Cover and end pages'
  if (pages.size === 0) return 'these pages'
  const numbers = [...pages].map((page) => {
    if (page === 'first') return 1
    if (page === 'last') return Math.max(1, pageCount)
    return Number(page) + 1
  })
  return `Page ${numbers.join(', ')}`
}
